import { Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LanguageContext from "components/LanguageContext";
import HelpAndShare from "components/LayoutFront/HelpAndShare";
import Wrapper from "components/LayoutFront/Wrapper";
import SiteContext from "components/SiteContext";
import allNews from "components/templates/news/allNews";
import CardVertical from "components/templatesComponents/CardVertical";
import { ImageContainer } from "components/templatesComponents/Image";
import TagLink from "components/templatesComponents/tagsDSFR/TagLink";
import TagsContainer from "components/templatesComponents/tagsDSFR/TagsContainer";
import TagsList from "components/templatesComponents/tagsDSFR/TagsList";
import { useFindPages, useFindPagesByTemplateKeys } from "hooks/page.hook";
import PropTypes from "prop-types";
import React, { useContext, useMemo } from "react";
import { numericFormatDate } from "utils/commonUtils";
import t from "utils/locales/translation.json";

const NewsTemplate = (props) => {
  const { page: pageVersion = {} } = props;

  const site = useContext(SiteContext);
  const { language } = useContext(LanguageContext);
  const { name: siteName } = site;

  const { id, title, shortDescription, image, contents, tags, publishedDate, pageId, page, template } = pageVersion;
  const { firstPublishedDate, parentId, lang, originalPageId, siteId } = page;
  const { watchTime, ratio, imageCaption, dynamicPart, dynamicAttachments } = contents;

  const { data: allNewsData = [] } = useFindPagesByTemplateKeys({
    templates: [allNews.key],
    siteName,
    filters: [`page.lang||eq||${lang}`],
  });

  const { data = [] } = useFindPagesByTemplateKeys({
    templates: ["NEWS"],
    siteName,
    filters: [`id||ne||${id}`, `page.lang||eq||${lang}`],
  });
  const { data: frenchPnuPages = [] } = useFindPages({
    siteId,
    pageId: lang === "FR" ? pageId : originalPageId,
    onlyPublishedPages: true,
    filters: [`page.lang||eq||FR`],
  });

  const getPublishedDate = useMemo(() => {
    if (frenchPnuPages.length === 1) {
      const { publishedDate: frenchPublishDate } = frenchPnuPages[0];
      return frenchPublishDate;
    }
    return publishedDate;
  }, [frenchPnuPages, publishedDate]);

  const getFirstPublishedDate = useMemo(() => {
    if (frenchPnuPages.length === 1) {
      const { page: frenchPage = {} } = frenchPnuPages[0];
      return frenchPage.firstPublishedDate;
    }
    return page.firstPublishedDate;
  }, [frenchPnuPages, page]);

  const publishedDateToDisplay = lang === "FR" ? publishedDate : getPublishedDate;
  const firstPublishedDateToDisplay = lang === "FR" ? firstPublishedDate : getFirstPublishedDate;

  const allNewsPages = allNewsData?.pages || allNewsData;

  const newsPages = data?.pages || data;

  const allNewsPath = allNewsPages?.find((p) => p.pageId === parentId)?.fullPath || allNewsPages[0]?.fullPath;

  const sameThemePages = React.useMemo(() => {
    const newPages = newsPages.map((p) => {
      const tagNumber =
        p.tags?.reduce((a, b) => (tags?.map((tag) => tag.toLowerCase())?.includes(b.toLowerCase()) ? a + 1 : a), 0) ||
        0;
      return { ...p, tagNumber };
    });
    return newPages
      .sort((newPage1, newPage2) => newPage2.tagNumber - newPage1.tagNumber)
      .filter((stp) => stp.tagNumber > 0)
      .slice(0, 3);
  }, [newsPages, tags]);

  return (
    <Wrapper template={template}>
      <Grid
        container
        sx={{ justifyContent: dynamicAttachments ? "space-between" : "center", px: { xs: 2, xl: 0 }, "& p": { mb: 3 } }}
      >
        <Grid size={{ xs: 12, lg: dynamicAttachments ? 7 : 8 }}>
          <h1>{title}</h1>
          {shortDescription}
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          justifyContent: dynamicAttachments ? "space-between" : "center",
          px: { xs: 2, xl: 0 },
          "& hr": { p: 0, pt: 0.125 },
        }}
      >
        <Grid size={{ xs: 12, lg: dynamicAttachments ? 7 : 8 }}>
          <hr />
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 0.5, md: 2 }}
            sx={{
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              pt: { xs: 1, lg: 2 },
              pb: 2,
              "& p::before": { "--icon-size": "1rem", mr: 0.5 },
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 0.5, md: 2 }}
              sx={{ alignItems: { xs: "flex-start", md: "center" } }}
            >
              {publishedDateToDisplay && (
                <p className="fr-text--xs fr-icon-calendar-event-line">
                  {t[language].common.news.updated_label} {numericFormatDate({ date: publishedDateToDisplay, lang })}
                </p>
              )}
              {firstPublishedDateToDisplay && (
                <Box
                  component="p"
                  className="fr-text--xs"
                  sx={{ ml: { xs: 2.5, md: 0 }, color: "var(--text-mention-grey)" }}
                >
                  {t[language].news.first_published} {numericFormatDate({ date: firstPublishedDateToDisplay, lang })}
                </Box>
              )}
            </Stack>
            {watchTime && (
              <p className="fr-text--xs fr-icon-timer-line">
                {t[language].news.read_label} : {watchTime}
              </p>
            )}
          </Stack>
          {image && (
            <>
              {typeof ratio?.props?.content === "object" && (
                <Grid container spacing={2} sx={{ alignItems: "center", mb: 1 }}>
                  <Grid>Ratio d&apos;image :</Grid>
                  <Grid size="grow">{ratio}</Grid>
                </Grid>
              )}
              <ImageContainer
                ratio={
                  (typeof ratio?.props?.content === "object" ? Number(ratio.props?.content.value) : Number(ratio)) || 52
                }
              >
                {image}
              </ImageContainer>
              {imageCaption && <figcaption className="fr-content-media__caption">{imageCaption}</figcaption>}
            </>
          )}
          {dynamicPart && <Box sx={{ mt: { xs: 4, lg: 5 } }}>{dynamicPart}</Box>}
        </Grid>
        {dynamicAttachments && (
          <Grid sx={{ "& .fr-follow": { mx: { xs: -2, lg: 0 } } }} size={{ xs: 12, lg: 4 }}>
            <Box sx={{ position: "sticky", top: 32, mt: { xs: 8, lg: 0 } }}>
              {dynamicAttachments && (
                <Box>
                  <Box component="p" className="fr-text--bold" sx={{ mb: 2 }}>
                    {t[language].news.pj_title}
                  </Box>
                  {dynamicAttachments}
                </Box>
              )}
              <HelpAndShare alwaysDisplayShare template={template} />
            </Box>
          </Grid>
        )}
      </Grid>
      {tags && (
        <Box sx={{ px: { xs: 2, xl: 0 } }}>
          <h2 className="fr-h5 fr-mt-7w">{t[language].news.same_theme_title}</h2>
          <TagsContainer>
            <TagsList Component={TagLink} tags={tags} url={allNewsPath} />
          </TagsContainer>
          {sameThemePages.length > 0 ? (
            <Grid container component="ul" className="fr-raw-list" spacing={2} sx={{ mt: 1 }}>
              {sameThemePages.map((p) => (
                <Grid key={p.id} component="li" size={{ xs: 12, md: 4 }}>
                  <CardVertical page={p} showImage={false} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box component="p" sx={{ textAlign: "center", fontStyle: "italic" }}>
              {t[language].news.no_article_message}
            </Box>
          )}
        </Box>
      )}
      <HelpAndShare alwaysDisplayShare template={template} />
    </Wrapper>
  );
};

NewsTemplate.propTypes = {
  page: PropTypes.shape(),
};

export default NewsTemplate;
